
import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import useFetchClasses from "../../hooks/useFetchClasses";
import { ApiUrl } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  getFeePeriodCount,
  getFeePeriodLabel,
  isMonthlyFeeFrequency,
} from "../../../utils/feeFrequencyUtils";

function AdmFeeStructure() {
  const {
    classes,
    loading: classLoading,
    error: classError,
  } = useFetchClasses();
  const navigate = useNavigate();
  const [selectedClass, setSelectedClass] = useState(null);
  // const [selectedCategory, setSelectedCategory] = useState(null);
  // const [selectedNewExisting, setSelectedNewExisting] = useState(null);
  const [isFeeStructureFrozen, setIsFeeStructureFrozen] = useState(false);
  const [feeStructure, setFeeStructure] = useState([]);

  const admissionNoRef = useRef(null);
  const versionRef = useRef(null);
  const toClassRef = useRef(null);

  const [, setError] = useState(null);

  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [elementNameOptions, setElementNameOptions] = useState([]);
  const [selectedNewExisting, setSelectedNewExisting] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Use refs for the fields that don't rely on state

  const [feeElement, setFeeElement] = useState({
    element_type_id: "",
    name: "",
    frequency: "",
    amount: "",
    periods: Array(6).fill(null),
  });

  const newExistingOptions = [
    { value: "N", label: "New" },
    { value: "E", label: "Existing" },
  ];

  const getCurrentAcademicSessionId = () =>
    localStorage.getItem("academicSessionId");

  useEffect(() => {
    // Fetch frequency options from API
    const fetchFrequencyOptions = async () => {
      try {
        const response = await fetch(
          `${ApiUrl.apiurl}FEEFREQUENCY/GetAllFrequencyPeriodlist/`
        );
        const result = await response.json();
        const options = result.data.map((option) => ({
          value: option.id,
          label: option.FeeFrequencyName,
        }));
        setFrequencyOptions(options);
      } catch (error) {
        console.error("Error fetching frequency options:", error);
      }
    };

    fetchFrequencyOptions();
  }, []);

  // Fetch category options on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${ApiUrl.apiurl}CATEGORY/GetAllCategory/`
        );
        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();
        const options = data.data.map((category) => ({
          value: category.id,
          label: category.categoryname,
        }));
        setCategoryOptions(options);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
  const fetchFeeElements = async () => {
  try {
    const response = await fetch(
      `${ApiUrl.apiurl}FEE_ELEMENT_TYPE/GetAllFeeElements/S`
    );
    const result = await response.json();

    if (result.message === "success") {
      const options = result.data.map((element) => ({
        value: element.id,
        label: element.element_name, // was element.element_description
      }));
      setElementNameOptions(options);
    }
  } catch (error) {
    console.error("Error fetching fee elements:", error);
  }
};

    fetchFeeElements();
  }, []);

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}Periods/GetAllPeriods/`);
        const data = await response.json();

        if (data.message === "success") {
          const academicYearId = localStorage.getItem("academicSessionId");
          const options = [...data.data]
            .filter(
              (period) =>
                !academicYearId ||
                String(period.academic_id) === String(academicYearId),
            )
            .sort(
              (first, second) =>
                (first.sorting_order ?? first.id) -
                (second.sorting_order ?? second.id),
            )
            .map((period) => ({
              value: period.id,
              label: period.period_name,
            }));
          setPeriodOptions(options);
        }
      } catch (error) {
        console.error("Error fetching periods:", error);
      }
    };

    fetchPeriods();
  }, []);

const handleFrequencyChange = (selectedOption) => {
  setFeeElement((prev) => ({
    ...prev,
    frequency: selectedOption?.value || "",
    periods: Array(6).fill(null),
  }));
};
const handleAddFeeElement = () => {
  // Basic validation
  if (!feeElement.element_type_id) {
    alert("Please select an Element Name before adding.");
    return;
  }
  if (!feeElement.frequency) {
    alert("Please select a Frequency before adding.");
    return;
  }
  if (
    feeElement.amount === "" ||
    feeElement.amount === null ||
    isNaN(feeElement.amount)
  ) {
    alert("Please enter a valid Amount before adding.");
    return;
  }

  const selectedFrequency = frequencyOptions.find(
    (option) => option.value === feeElement.frequency,
  );
  const periodCount = getFeePeriodCount(selectedFrequency);
  const selectedPeriods = isMonthlyFeeFrequency(selectedFrequency)
    ? periodOptions.map((option) => option.value)
    : feeElement.periods.slice(0, periodCount);
  if (
    !periodCount ||
    selectedPeriods.some((periodId) => !periodId)
  ) {
    alert("Please select every required Period Month.");
    return;
  }

  // Add the fee element to the structure
  setFeeStructure((prev) => [
    ...prev,
    {
      ...feeElement,
      periods: selectedPeriods,
      frequencyName: selectedFrequency?.label || "",
    },
  ]);

  // Reset feeElement for next input
  setFeeElement({
    element_type_id: "",
    name: "",
    frequency: "",
    amount: "",
    periods: Array(6).fill(null),
    adjustment_flag: "N",
  });
};


  const handleClear = () => {
    // Reset all fee element fields
    setFeeElement({
      element_type_id: "",
      name: "",
      frequency: "",
      amount: "",
      periods: Array(6).fill(null),
    });

    // Reset selected values
    setSelectedCategory(null);
    setSelectedNewExisting(null);
    setSelectedClass(null);

    // Clear input references
    admissionNoRef.current.value = "";
    versionRef.current.value = "";

    // Clear React Select dropdowns (if using refs)
    if (toClassRef.current) {
      toClassRef.current.clearValue();
    }

    // Optionally clear the fee structure table
    setFeeStructure([]);
  };

  // const handleSave = async () => {
  //   if (!selectedCategory) {
  //     alert("Please select a category before saving.");
  //     return;
  //   }

  //   const feestructureDetails = feeStructure.map((element, index) => {
  //     const frequencyOption = frequencyOptions.find(
  //       (option) => option.label === element.frequency
  //     );

  //     return {
  //       id: index + 1,
  //       element_type_id: element.element_type_id,
  //       element_frequency: frequencyOption ? frequencyOption.value : 1,
  //       amount: Number(element.amount).toFixed(2) || "0.00",
  //       period_1: element.periods[0] || null,
  //       period_2: element.periods[1] || null,
  //       period_3: element.periods[2] || null,
  //       period_4: element.periods[3] || null,
  //       period_5: element.periods[4] || null,
  //       period_6: element.periods[5] || null,
  //       adjustment_flag: element.adjustment_flag || "N",
  //       is_active: true,
  //       created_by: 19,
  //     };
  //   });

  //   const payload = {
  //     feemasterStructureDetails: {
  //       fee_structure_code: "FSC2024",
  //       fee_structure_desc: admissionNoRef.current.value,
  //       enabled: isFeeStructureFrozen ? "Y" : "N",
  //       org_id: 1,
  //       branch_id: 1,
  //       class_id: selectedClass.value,
  //       academic_year_id: selectedSessionId,
  //       version_no: versionRef.current.value,
  //       category_code: selectedCategory.value,
  //       new_existing: selectedNewExisting?.value || "",
  //       created_by: 19,
  //     },
  //     feestructureDetails,
  //   };

  //   try {
  //     const response = await fetch(
  //       `${ApiUrl.apiurl}FEESTRUCTURE/FeeStructureCreate/`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!response.ok) throw new Error("Network response was not ok");

  //     const responseData = await response.json();
  //     console.log("Success:", responseData);
  //     alert("Fee structure added successfully!");
  //   } catch (error) {
  //     console.error("Error:", error);
  //     setError("Failed to save fee structure. Please try again.");
  //   }
  // };

  // const handleSave = async () => {
  //   if (!selectedCategory) {
  //     alert("Please select a category before saving.");
  //     return;
  //   }

  //   // Get org_id and branch_id from local storage
  //   const orgId = localStorage.getItem("orgId") || 1;
  //   const branchId = localStorage.getItem("branchId") || 1;

  //   const feestructureDetails = feeStructure.map((element, index) => {
  //     const frequencyOption = frequencyOptions.find(
  //       (option) => option.label === element.frequency
  //     );

  //     return {
  //       id: index + 1,
  //       element_type_id: element.element_type_id,
  //       element_frequency: frequencyOption ? frequencyOption.value : 1,
  //       amount: Number(element.amount).toFixed(2) || "0.00",
  //       period_1: element.periods[0] || null,
  //       period_2: element.periods[1] || null,
  //       period_3: element.periods[2] || null,
  //       period_4: element.periods[3] || null,
  //       period_5: element.periods[4] || null,
  //       period_6: element.periods[5] || null,
  //       adjustment_flag: element.adjustment_flag || "N",
  //       is_active: true,
  //       created_by: 19,
  //     };
  //   });

  //   const payload = {
  //     feemasterStructureDetails: {
  //       fee_structure_code: "FSC2024",
  //       fee_structure_desc: admissionNoRef.current.value,
  //       enabled: isFeeStructureFrozen ? "Y" : "N",
  //       org_id: orgId,
  //       branch_id: branchId,
  //       class_id: selectedClass?.value || null,
  //       academic_year_id: selectedSessionId,
  //       version_no: versionRef.current.value,
  //       category_code: selectedCategory?.value || null,
  //       new_existing: selectedNewExisting?.value || "",
  //       created_by: 19,
  //     },
  //     feestructureDetails,
  //   };

  //   try {
  //     const response = await fetch(
  //       `${ApiUrl.apiurl}FEESTRUCTURE/FeeStructureCreate/`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!response.ok) throw new Error("Network response was not ok");

  //     const responseData = await response.json();
  //     console.log("Success:", responseData);
  //     alert("Fee structure added successfully!");

  //     // Clear form data after saving
  //     setSelectedSession(null);
  //     setSelectedClass(null);
  //     setSelectedCategory(null);
  //     setSelectedNewExisting("");
  //     setIsFeeStructureFrozen(false);
  //     setFeeStructure([]);
  //     setSelectedSessionId(null);
  //     setSelectedClassId(null);
  //     setDisabledPeriods(Array(6).fill(true));

  //     // Reset input fields
  //     if (admissionNoRef.current) admissionNoRef.current.value = "";
  //     if (versionRef.current) versionRef.current.value = "";
  //     if (toClassRef.current) toClassRef.current.value = "";
  //   } catch (error) {
  //     console.error("Error:", error);
  //     setError("Failed to save fee structure. Please try again.");
  //   }
  // };

  //14aug
  // const handleSave = async () => {
  //   if (!selectedCategory) {
  //     alert("Please select a category before saving.");
  //     return;
  //   }

  //   // Validate fee structure entries
  //   for (let i = 0; i < feeStructure.length; i++) {
  //     const entry = feeStructure[i];
  //     if (
  //       !entry.element_type_id ||
  //       !entry.frequency ||
  //       entry.amount === "" ||
  //       entry.amount === null ||
  //       isNaN(entry.amount)
  //     ) {
  //       alert(
  //         `Please fill in all required fields (element, frequency, amount) for row ${
  //           i + 1
  //         }.`
  //       );
  //       return;
  //     }
  //   }

  //   // Get org_id and branch_id from local storage
  //   const orgId = localStorage.getItem("orgId") || 1;
  //   const branchId = localStorage.getItem("branchId") || 1;

  //   const feestructureDetails = feeStructure.map((element, index) => {
  //     const frequencyOption = frequencyOptions.find(
  //       (option) => option.label === element.frequency
  //     );

  //     return {
  //       id: index + 1,
  //       element_type_id: element.element_type_id,
  //       element_frequency: frequencyOption ? frequencyOption.value : 1,
  //       amount: Number(element.amount).toFixed(2) || "0.00",
  //       period_1: element.periods[0] || null,
  //       period_2: element.periods[1] || null,
  //       period_3: element.periods[2] || null,
  //       period_4: element.periods[3] || null,
  //       period_5: element.periods[4] || null,
  //       period_6: element.periods[5] || null,
  //       adjustment_flag: element.adjustment_flag || "N",
  //       is_active: true,
  //       created_by: 19,
  //     };
  //   });

  //   const payload = {
  //     feemasterStructureDetails: {
  //       fee_structure_code: "FSC2024",
  //       fee_structure_desc: admissionNoRef.current.value,
  //       enabled: isFeeStructureFrozen ? "Y" : "N",
  //       org_id: orgId,
  //       branch_id: branchId,
  //       class_id: selectedClass?.value || null,
  //       academic_year_id: selectedSessionId,
  //       version_no: versionRef.current.value,
  //       category_code: selectedCategory?.value || null,
  //       new_existing: selectedNewExisting?.value || "",
  //       created_by: 19,
  //     },
  //     feestructureDetails,
  //   };

  //   try {
  //     const response = await fetch(
  //       `${ApiUrl.apiurl}FEESTRUCTURE/FeeStructureCreate/`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!response.ok) throw new Error("Network response was not ok");

  //     const responseData = await response.json();
  //     console.log("Success:", responseData);
  //     alert("Fee structure added successfully!");

  //     // Clear form data after saving
  //     setSelectedSession(null);
  //     setSelectedClass(null);
  //     setSelectedCategory(null);
  //     setSelectedNewExisting("");
  //     setIsFeeStructureFrozen(false);
  //     setFeeStructure([]);
  //     setSelectedSessionId(null);
  //     setSelectedClassId(null);
  //     setDisabledPeriods(Array(6).fill(true));

  //     // Reset input fields
  //     if (admissionNoRef.current) admissionNoRef.current.value = "";
  //     if (versionRef.current) versionRef.current.value = "";
  //     if (toClassRef.current) toClassRef.current.value = "";
  //   } catch (error) {
  //     console.error("Error:", error);
  //     setError("Failed to save fee structure. Please try again.");
  //   }
  // };

  const handleSave = async () => {
    // === Validate feemasterStructureDetails ===
    const academicSessionId = getCurrentAcademicSessionId();

    if (!academicSessionId) {
      alert("Please select an academic session from the navbar before saving.");
      return;
    }
    if (!selectedClass?.value) {
      alert("Please select a class before saving.");
      return;
    }
    if (!selectedCategory?.value) {
      alert("Please select a category before saving.");
      return;
    }
    if (!versionRef.current?.value.trim()) {
      alert("Please enter a version number.");
      return;
    }
    if (!admissionNoRef.current?.value.trim()) {
      alert("Please enter the fee structure description.");
      return;
    }

    // === Validate feestructureDetails ===
    if (feeStructure.length === 0) {
      alert("Please add at least one fee structure entry.");
      return;
    }

    for (let i = 0; i < feeStructure.length; i++) {
      const entry = feeStructure[i];
      if (
        !entry.element_type_id ||
        !entry.frequency ||
        entry.amount === "" ||
        entry.amount === null ||
        isNaN(entry.amount)
      ) {
        alert(
          `Please fill in all required fields (element, frequency, amount) for row ${
            i + 1
          }.`
        );
        return;
      }
    }

    // === Prepare payload ===
    const orgId = localStorage.getItem("orgId") || 1;
    const branchId = localStorage.getItem("branchId") || 1;

    const feestructureDetails = feeStructure.map((element, index) => {
      const isMonthly = isMonthlyFeeFrequency(
        frequencyOptions.find((option) => option.value === element.frequency),
      );
      return {
        id: index + 1,
        element_type_id: element.element_type_id,
        element_frequency: element.frequency,
        amount: Number(element.amount).toFixed(2) || "0.00",
        period_1: isMonthly ? null : element.periods[0] || null,
        period_2: isMonthly ? null : element.periods[1] || null,
        period_3: isMonthly ? null : element.periods[2] || null,
        period_4: isMonthly ? null : element.periods[3] || null,
        period_5: isMonthly ? null : element.periods[4] || null,
        period_6: isMonthly ? null : element.periods[5] || null,
        adjustment_flag: element.adjustment_flag || "N",
        is_active: true,
        created_by: 19,
      };
    });

    const payload = {
      feemasterStructureDetails: {
        fee_structure_code: "FSC2024",
        fee_structure_desc: admissionNoRef.current.value,
        enabled: isFeeStructureFrozen ? "Y" : "N",
        org_id: orgId,
        branch_id: branchId,
        class_id: selectedClass?.value || null,
        academic_year_id: academicSessionId,
        version_no: versionRef.current.value,
        category_code: selectedCategory?.value || null,
        new_existing: selectedNewExisting?.value || "",
        created_by: 19,
      },
      feestructureDetails,
    };

    try {
      const response = await fetch(
        `${ApiUrl.apiurl}FEESTRUCTURE/FeeStructureCreate/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const responseData = await response.json();
      console.log("Success:", responseData);
      alert("Fee structure added successfully!");

      // Reset form
      setSelectedClass(null);
      setSelectedCategory(null);
      setSelectedNewExisting(null);
      setIsFeeStructureFrozen(false);
      setFeeStructure([]);

      if (admissionNoRef.current) admissionNoRef.current.value = "";
      if (versionRef.current) versionRef.current.value = "";
      if (toClassRef.current) toClassRef.current.value = "";
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to save fee structure. Please try again.");
    }
  };

  const selectedFormFrequency = frequencyOptions.find(
    (option) => option.value === feeElement.frequency,
  );
  const formPeriodCount = isMonthlyFeeFrequency(selectedFormFrequency)
    ? 0
    : getFeePeriodCount(selectedFormFrequency);
  const maxPeriodCount = Math.max(
    1,
    ...feeStructure.map((element) => element.periods?.length || 0),
    formPeriodCount,
  );

  return (
    <div>
      {/* Button Section */}
      <div className="row mb-3 mx-0">
        <div className="col-12 mb-3 mt-3 d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary me-2"
            style={{
              width: "150px",
            }}
            onClick={handleSave}
          >
            Save
          </button>

          <button
            type="button"
            className="btn btn-secondary me-2"
            style={{
              width: "150px",
            }}
            onClick={handleClear}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-danger me-2"
            style={{
              width: "150px",
            }}
            onClick={() => navigate("/admin/dashboard")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="row mt-2 mx-2">
        <div className="col-12 custom-section-box">
          <div className="row">
            {/* Fee Structure Name */}
            <div className="col-12 col-md-3 mb-3">
              <label htmlFor="fee-structure-name" className="form-label">
                Fee Structure Name
              </label>
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  id="fee-structure-name"
                  className="form-control detail"
                  placeholder="Enter Fee Structure Name"
                  ref={admissionNoRef}
                />
              </div>
            </div>

            {/* Class */}
            <div className="col-12 col-md-3 mb-2">
              <label htmlFor="admitted-class" className="form-label">
                Class
              </label>
              {classLoading ? (
                <p>Loading classes...</p>
              ) : classError ? (
                <p>Error loading classes: {classError}</p>
              ) : (
                <Select
                  id="admitted-class"
                  options={classes.map((classItem) => ({
                    value: classItem.id,
                    label: classItem.classname,
                  }))}
                  value={selectedClass || null} // Ensure it’s not undefined or empty
                  placeholder="Select Class"
                  className="detail"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    setSelectedClass(selectedOption); // Update selected class
                    console.log("Selected Class: ", selectedOption);
                  }}
                />
              )}
            </div>

            {/* Category */}
            <div className="col-12 col-md-3 mb-2">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <Select
                id="category"
                className="detail"
                options={categoryOptions}
                value={selectedCategory} // Bind to the selectedCategory state
                placeholder="Select Category"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  setSelectedCategory(selectedOption); // Update selected category
                }}
              />
            </div>
            {/* New/Existing */}
            <div className="col-12 col-md-3 mb-4">
              <label htmlFor="new-existing" className="form-label">
                New/Existing
              </label>
              <Select
                id="new-existing"
                className="detail"
                options={newExistingOptions}
                placeholder="Select New/Existing"
                classNamePrefix="react-select"
                ref={toClassRef}
                value={selectedNewExisting}
                onChange={(selectedOption) => {
                  setSelectedNewExisting(selectedOption);
                  console.log("Selected New/Existing: ", selectedOption);
                }}
              />
            </div>

            {/* Version */}
            <div className="col-12 col-md-3 mb-2">
              <label htmlFor="version" className="form-label">
                Version
              </label>
              <input
                type="text"
                id="version"
                className="form-control detail"
                placeholder="Enter Version"
                ref={versionRef}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Fee Elements Section */}
      <div className="mt-4">
        {/* Add Fee Elements Table */}
        <div className="add-fee-elements ">
          <h5>Add Fee Elements</h5>
          {/* Add Bootstrap table-responsive class for responsive behavior */}
          <div className="table-responsive">
            <table className="table mt-2 table-bordered ">
              <thead>
                <tr>
                  <th style={{ width: "200px" }}>Element Name</th>
                  <th style={{ width: "180px" }}>Frequency</th>
                  <th>Amount</th>
                  {Array.from({ length: maxPeriodCount }).map((_, idx) => (
                    <th key={`input-period-header-${idx}`}>
                      Period Month {idx + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <select
                      className="detail"
                      style={{ width: "200px" }}
                      value={feeElement.element_type_id?.toString() || ""}
                      onChange={(e) => {
                        const selectedOption = elementNameOptions.find(
                          (option) => option.value.toString() === e.target.value
                        );
                        setFeeElement((prev) => ({
                          ...prev,
                          element_type_id: selectedOption?.value || "",
                          name: selectedOption?.label || "",
                        }));
                      }}
                    >
                      <option value="">Select Element</option>
                      {elementNameOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="detail"
                      style={{ width: "180px" }}
                      value={feeElement.frequency?.toString() || ""}
                      onChange={(e) => {
                        const selectedOption = frequencyOptions.find(
                          (option) => option.value.toString() === e.target.value
                        );
                        handleFrequencyChange(selectedOption);
                      }}
                    >
                      <option value="">Select Frequency</option>
                      {frequencyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      id="amount"
                      className="form-control detail"
                      placeholder="Amount"
                      value={feeElement.amount}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={(e) =>
                        setFeeElement((prev) => ({
                          ...prev,
                          amount:
                            e.target.value === ""
                              ? ""
                              : parseFloat(e.target.value),
                        }))
                      }
                    />
                  </td>
                  {/* {Array.from({ length: 6 }).map((_, idx) => (
                    <td key={idx}>
                      <Select
                        options={periodOptions}
                        value={periodOptions.find(
                          (option) => option.value === feeElement.periods[idx]
                        )}
                        onChange={(selectedOption) => {
                          const updatedPeriods = [...feeElement.periods];
                          updatedPeriods[idx] = selectedOption
                            ? selectedOption.value
                            : null;
                          setFeeElement({
                            ...feeElement,
                            periods: updatedPeriods,
                          });
                        }}
                      />
                    </td>
                  ))} */}

                  {Array.from({
                    length: formPeriodCount,
                  }).map((_, idx) => (
                    <td key={idx}>
                      <Select
                        options={periodOptions}
                        className="detail"
                        value={
                          periodOptions.find(
                            (option) => option.value === feeElement.periods[idx]
                          ) || null
                        }
                        onChange={(selectedOption) => {
                          const updatedPeriods = [...feeElement.periods];
                          updatedPeriods[idx] = selectedOption
                            ? selectedOption.value
                            : null;

                          setFeeElement({
                            ...feeElement,
                            periods: updatedPeriods,
                          });
                        }}
                        classNamePrefix="react-select"
                        placeholder="Select"
                      />
                    </td>
                  ))}
                </tr>
                {/* Add Button */}
                <tr>
                  <td colSpan={maxPeriodCount + 3}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddFeeElement}
                    >
                      Add
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Fee Structure Details Table */}
        {feeStructure.length > 0 && (
          <div className="fee-structure-details mt-4 mx-2">
            <h5>Fee Structure Details</h5>
            {/* Add Bootstrap table-responsive class for responsive behavior */}
            <div className="table-responsive">
              <table className="table mt-2 table-bordered">
                <thead>
                  <tr>
                    <th>Element Name</th>
                    <th>Frequency</th>
                    <th>Amount</th>
                    {Array.from({ length: maxPeriodCount }).map((_, idx) => (
                      <th key={`detail-period-header-${idx}`}>
                        Period Month {idx + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {feeStructure.map((element, index) => (
                    <tr key={index}>
                      <td>{element.name}</td>
                      <td>
                        {frequencyOptions.find(
                          (option) => option.value === element.frequency
                        )?.label || ""}
                      </td>
                      <td>{element.amount}</td>
                      {Array.from({ length: maxPeriodCount }).map(
                        (_, periodIndex) => {
                          const periodOption = periodOptions.find(
                            (option) =>
                              option.value === element.periods?.[periodIndex],
                          );
                          return (
                            <td key={periodIndex}>
                              {getFeePeriodLabel(
                                periodOption,
                                element.frequencyName ||
                                  frequencyOptions.find(
                                    (option) => option.value === element.frequency,
                                  )?.label,
                                periodIndex,
                              )}
                            </td>
                          );
                        },
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdmFeeStructure;


// import React, { useState, useRef, useEffect } from "react";
// import Select from "react-select";
// import useFetchClasses from "../../hooks/useFetchClasses";
// import { ApiUrl } from "../../../ApiUrl";
// import { useNavigate } from "react-router-dom";

// function AdmFeeStructure() {
//   const {
//     classes,
//     loading: classLoading,
//     error: classError,
//   } = useFetchClasses();
//   const navigate = useNavigate();
//   const [selectedSession, setSelectedSession] = useState(null);
//   const [selectedClass, setSelectedClass] = useState(null);
//   // const [selectedCategory, setSelectedCategory] = useState(null);
//   // const [selectedNewExisting, setSelectedNewExisting] = useState(null);
//   const [isFeeStructureFrozen, setIsFeeStructureFrozen] = useState(false);
//   const [feeStructure, setFeeStructure] = useState([]);

//   const admissionNoRef = useRef(null);
//   const versionRef = useRef(null);
//   const toClassRef = useRef(null);

//   const [sessions, setSessions] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [disabledPeriods, setDisabledPeriods] = useState(Array(6).fill(true));

//   // State for selected class and session
//   const [selectedClassId, setSelectedClassId] = useState(null);
//   const [selectedSessionId, setSelectedSessionId] = useState(null);
//   const [frequencyOptions, setFrequencyOptions] = useState([]);
//   const [periodOptions, setPeriodOptions] = useState([]);
//   const [elementNameOptions, setElementNameOptions] = useState([]);
//   const [selectedNewExisting, setSelectedNewExisting] = useState("");
//   const [categoryOptions, setCategoryOptions] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);

//   // Use refs for the fields that don't rely on state

//   const [feeElement, setFeeElement] = useState({
//     element_type_id: "",
//     name: "",
//     frequency: "",
//     amount: "",
//     periods: Array(6).fill(null),
//   });

//   const newExistingOptions = [
//     { value: "N", label: "New" },
//     { value: "E", label: "Existing" },
//   ];

//   useEffect(() => {
//     // Fetch frequency options from API
//     const fetchFrequencyOptions = async () => {
//       try {
//         const response = await fetch(
//           `${ApiUrl.apiurl}FEEFREQUENCY/GetAllFrequencyPeriodlist/`
//         );
//         const result = await response.json();
//         const options = result.data.map((option) => ({
//           value: option.id,
//           label: option.FeeFrequencyName,
//           feeFrequencyName: option.FeeFrequencyName,
//         }));
//         setFrequencyOptions(options);
//       } catch (error) {
//         console.error("Error fetching frequency options:", error);
//       }
//     };

//     fetchFrequencyOptions();
//   }, []);

//   // Fetch category options on component mount
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await fetch(
//           `${ApiUrl.apiurl}CATEGORY/GetAllCategory/`
//         );
//         if (!response.ok) throw new Error("Network response was not ok");

//         const data = await response.json();
//         const options = data.data.map((category) => ({
//           value: category.id,
//           label: category.categoryname,
//         }));
//         setCategoryOptions(options);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       }
//     };
//     fetchCategories();
//   }, []);

//   useEffect(() => {
//     const fetchSessions = async () => {
//       try {
//         const response = await fetch(
//           `${ApiUrl.apiurl}AcademicYear/GetAllAcademicYear/`
//         );
//         const result = await response.json();

//         if (result.message === "Success") {
//           const options = result.data.map((session) => ({
//             value: session.id,
//             label: session.sesion_code,
//           }));
//           setSessions(options);
//         } else {
//           setError(result.message);
//         }
//       } catch (err) {
//         console.error("Error fetching sessions:", err);
//         setError("Error fetching sessions");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchSessions();

//     return () => {
//       setSessions([]);
//       setLoading(true);
//       setError(null);
//     };
//   }, []);

//   useEffect(() => {
//     const fetchFeeElements = async () => {
//       try {
//         const response = await fetch(
//           `${ApiUrl.apiurl}FEE_ELEMENT_TYPE/GetAllFeeElements/S`
//         );
//         const result = await response.json();

//         if (result.message === "success") {
//           const options = result.data.map((element) => ({
//             value: element.id,
//             label: element.element_name,
//           }));
//           setElementNameOptions(options);
//         }
//       } catch (error) {
//         console.error("Error fetching fee elements:", error);
//       }
//     };

//     fetchFeeElements();
//   }, []);

//   useEffect(() => {
//     const fetchPeriods = async () => {
//       try {
//         const response = await fetch(`${ApiUrl.apiurl}Periods/GetAllPeriods/`);
//         const data = await response.json();

//         if (data.message === "success") {
//           const options = data.data.map((period) => ({
//             value: period.id,
//             label: period.period_name,
//           }));
//           setPeriodOptions(options);
//           setDisabledPeriods(Array(6).fill(true));
//         }
//       } catch (error) {
//         console.error("Error fetching periods:", error);
//       }
//     };

//     fetchPeriods();
//   }, []);

// const getEnabledPeriodCount = (selectedOption) => {
//     if (!selectedOption) return 0;

//     const id = selectedOption.value;

//     // Based on API IDs:
//     // 1 = Yearly    → 1 period
//     // 2 = Monthly   → 1 period
//     // 3 = Quarterly → 4 periods
//     // 4 = Half Yearly → 2 periods
//     const periodCountById = {
//       1: 1,
//       2: 1,
//       3: 4,
//       4: 2,
//     };

//     return periodCountById[id] || 0;
//   };

//   const handleFrequencyChange = (selectedOption) => {
//     if (!selectedOption) {
//       setDisabledPeriods(Array(6).fill(true));
//       setFeeElement((prev) => ({
//         ...prev,
//         frequency: "",
//         periods: Array(6).fill(null),
//       }));
//       return;
//     }

//     const enabledPeriodCount = getEnabledPeriodCount(selectedOption);

//     const updatedDisabledPeriods = Array.from(
//       { length: 6 },
//       (_, idx) => idx >= enabledPeriodCount
//     );

//     setDisabledPeriods(updatedDisabledPeriods);
//     setFeeElement((prev) => ({
//       ...prev,
//       frequency: selectedOption.value,
//       periods: prev.periods.map((periodValue, idx) =>
//         idx < enabledPeriodCount ? periodValue : null
//       ),
//     }));
//   };

//   const handleAddFeeElement = () => {
//     // Basic validation
//     if (!feeElement.element_type_id) {
//       alert("Please select an Element Name before adding.");
//       return;
//     }
//     if (!feeElement.frequency) {
//       alert("Please select a Frequency before adding.");
//       return;
//     }
//     if (
//       feeElement.amount === "" ||
//       feeElement.amount === null ||
//       isNaN(feeElement.amount)
//     ) {
//       alert("Please enter a valid Amount before adding.");
//       return;
//     }

//     // Add the fee element to the structure
//     setFeeStructure((prev) => [...prev, { ...feeElement }]);

//     // Reset feeElement for next input
//     setFeeElement({
//       element_type_id: "",
//       name: "",
//       frequency: "",
//       amount: "",
//       periods: Array(6).fill(null),
//       adjustment_flag: "N",
//     });
//   };

//   const handleClear = () => {
//     // Reset all fee element fields
//     setFeeElement({
//       element_type_id: "",
//       name: "",
//       frequency: "",
//       amount: "",
//       periods: Array(6).fill(null),
//     });

//     // Reset selected values
//     setSelectedCategory(null);
//     setSelectedNewExisting(null);
//     setSelectedSession(null);
//     setSelectedClass(null);

//     // Clear input references
//     admissionNoRef.current.value = "";
//     versionRef.current.value = "";

//     // Clear React Select dropdowns (if using refs)
//     if (toClassRef.current) {
//       toClassRef.current.clearValue();
//     }

//     // Optionally clear the fee structure table
//     setFeeStructure([]);
//   };


//   const handleSave = async () => {
//     // === Validate feemasterStructureDetails ===
//     if (!selectedSessionId) {
//       alert("Please select a session before saving.");
//       return;
//     }
//     if (!selectedClass?.value) {
//       alert("Please select a class before saving.");
//       return;
//     }
//     if (!selectedCategory?.value) {
//       alert("Please select a category before saving.");
//       return;
//     }
//     if (!versionRef.current?.value.trim()) {
//       alert("Please enter a version number.");
//       return;
//     }
//     if (!admissionNoRef.current?.value.trim()) {
//       alert("Please enter the fee structure description.");
//       return;
//     }

//     // === Validate feestructureDetails ===
//     if (feeStructure.length === 0) {
//       alert("Please add at least one fee structure entry.");
//       return;
//     }

//     for (let i = 0; i < feeStructure.length; i++) {
//       const entry = feeStructure[i];
//       if (
//         !entry.element_type_id ||
//         !entry.frequency ||
//         entry.amount === "" ||
//         entry.amount === null ||
//         isNaN(entry.amount)
//       ) {
//         alert(
//           `Please fill in all required fields (element, frequency, amount) for row ${
//             i + 1
//           }.`
//         );
//         return;
//       }
//     }

//     // === Prepare payload ===
//     const orgId = localStorage.getItem("orgId") || 1;
//     const branchId = localStorage.getItem("branchId") || 1;

//   const feestructureDetails = feeStructure.map((element, index) => {
//     return {
//       id: index + 1,
//       element_type_id: element.element_type_id,
//       element_frequency: element.frequency, //  directly use stored value
//       amount: Number(element.amount).toFixed(2) || "0.00",
//       period_1: element.periods[0] || null,
//       period_2: element.periods[1] || null,
//       period_3: element.periods[2] || null,
//       period_4: element.periods[3] || null,
//       period_5: element.periods[4] || null,
//       period_6: element.periods[5] || null,
//       adjustment_flag: element.adjustment_flag || "N",
//       is_active: true,
//       created_by: 19,
//     };
//   });


//     const payload = {
//       feemasterStructureDetails: {
//         fee_structure_code: "FSC2024",
//         fee_structure_desc: admissionNoRef.current.value,
//         enabled: isFeeStructureFrozen ? "Y" : "N",
//         org_id: orgId,
//         branch_id: branchId,
//         class_id: selectedClass?.value || null,
//         academic_year_id: selectedSessionId,
//         version_no: versionRef.current.value,
//         category_code: selectedCategory?.value || null,
//         new_existing: selectedNewExisting?.value || "",
//         created_by: 19,
//       },
//       feestructureDetails,
//     };

//     try {
//       const response = await fetch(
//         `${ApiUrl.apiurl}FEESTRUCTURE/FeeStructureCreate/`,
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) throw new Error("Network response was not ok");

//       const responseData = await response.json();
//       console.log("Success:", responseData);
//       alert("Fee structure added successfully!");

//       // Reset form
//       setSelectedSession(null);
//       setSelectedClass(null);
//       setSelectedCategory(null);
//       setSelectedNewExisting(null);
//       setIsFeeStructureFrozen(false);
//       setFeeStructure([]);
//       setSelectedSessionId(null);
//       setSelectedClassId(null);
//       setDisabledPeriods(Array(6).fill(true));

//       if (admissionNoRef.current) admissionNoRef.current.value = "";
//       if (versionRef.current) versionRef.current.value = "";
//       if (toClassRef.current) toClassRef.current.value = "";
//     } catch (error) {
//       console.error("Error:", error);
//       setError("Failed to save fee structure. Please try again.");
//     }
//   };

//   const handleElementTypeChange = (selectedOption) => {
//     setFeeElement((prev) => ({
//       ...prev,
//       element_type_id: selectedOption.value,
//     }));
//   };

//   // Add handlers for selecting classes and sessions
//   const handleClassChange = (selectedOption) => {
//     setSelectedClassId(selectedOption.value);
//   };

//   const handleSessionChange = (selectedOption) => {
//     setSelectedSessionId(selectedOption.value);
//   };

//   return (
//     <div>
//       {/* Button Section */}
//       <div className="row mb-3 mx-0">
//         <div className="col-12 mb-3 mt-3 d-flex flex-wrap gap-2">
//           <button
//             type="button"
//             className="btn btn-primary me-2"
//             style={{
//               width: "150px",
//             }}
//             onClick={handleSave}
//           >
//             Save
//           </button>

//           <button
//             type="button"
//             className="btn btn-secondary me-2"
//             style={{
//               width: "150px",
//             }}
//             onClick={handleClear}
//           >
//             Clear
//           </button>
//           <button
//             type="button"
//             className="btn btn-danger me-2"
//             style={{
//               width: "150px",
//             }}
//             onClick={() => navigate("/admin/dashboard")}
//           >
//             Close
//           </button>
//         </div>
//       </div>

//       <div className="row mt-2 mx-2">
//         <div className="col-12 custom-section-box">
//           <div className="row">
//             {/* Fee Structure Name */}
//             <div className="col-12 col-md-3 mb-3">
//               <label htmlFor="fee-structure-name" className="form-label">
//                 Fee Structure Name
//               </label>
//               <div className="d-flex align-items-center">
//                 <input
//                   type="text"
//                   id="fee-structure-name"
//                   className="form-control detail"
//                   placeholder="Enter Fee Structure Name"
//                   ref={admissionNoRef}
//                 />
//               </div>
//             </div>

//             {/* Session */}
//             <div className="col-12 col-md-3 mb-2">
//               <label htmlFor="session" className="form-label">
//                 Session
//               </label>

//               {/* <Select
//                 id="session"
//                 options={sessions}
//                 placeholder="Select Session"
//                 isClearable
//                 classNamePrefix="react-select"
//                 onChange={(selectedOption) => {
//                   console.log("Selected Session: ", selectedOption);
//                   handleSessionChange(selectedOption);
//                 }}
//               /> */}

//               <Select
//                 id="session"
//                 options={sessions}
//                 className="detail"
//                 value={selectedSession} // Bind to state
//                 placeholder="Select Session"
//                 classNamePrefix="react-select"
//                 onChange={(selectedOption) => {
//                   setSelectedSession(selectedOption); // Update state
//                   handleSessionChange(selectedOption);
//                 }}
//               />
//             </div>

//             {/* Class */}
//             <div className="col-12 col-md-3 mb-2">
//               <label htmlFor="admitted-class" className="form-label">
//                 Class
//               </label>
//               {classLoading ? (
//                 <p>Loading classes...</p>
//               ) : classError ? (
//                 <p>Error loading classes: {classError}</p>
//               ) : (
//                 <Select
//                   id="admitted-class"
//                   options={classes.map((classItem) => ({
//                     value: classItem.id,
//                     label: classItem.classname,
//                   }))}
//                   value={selectedClass || null} // Ensure it’s not undefined or empty
//                   placeholder="Select Class"
//                   className="detail"
//                   classNamePrefix="react-select"
//                   onChange={(selectedOption) => {
//                     setSelectedClass(selectedOption); // Update selected class
//                     console.log("Selected Class: ", selectedOption);
//                   }}
//                 />
//               )}
//             </div>

//             {/* Category */}
//             <div className="col-12 col-md-3 mb-2">
//               <label htmlFor="category" className="form-label">
//                 Category
//               </label>
//               <Select
//                 id="category"
//                 className="detail"
//                 options={categoryOptions}
//                 value={selectedCategory} // Bind to the selectedCategory state
//                 placeholder="Select Category"
//                 classNamePrefix="react-select"
//                 onChange={(selectedOption) => {
//                   setSelectedCategory(selectedOption); // Update selected category
//                 }}
//               />
//             </div>
//             {/* New/Existing */}
//             <div className="col-12 col-md-3 mb-4">
//               <label htmlFor="new-existing" className="form-label">
//                 New/Existing
//               </label>
//               <Select
//                 id="new-existing"
//                 className="detail"
//                 options={newExistingOptions}
//                 placeholder="Select New/Existing"
//                 classNamePrefix="react-select"
//                 ref={toClassRef}
//                 value={selectedNewExisting}
//                 onChange={(selectedOption) => {
//                   setSelectedNewExisting(selectedOption);
//                   console.log("Selected New/Existing: ", selectedOption);
//                 }}
//               />
//             </div>

//             {/* Version */}
//             <div className="col-12 col-md-3 mb-2">
//               <label htmlFor="version" className="form-label">
//                 Version
//               </label>
//               <input
//                 type="text"
//                 id="version"
//                 className="form-control detail"
//                 placeholder="Enter Version"
//                 ref={versionRef}
//               />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Add Fee Elements Section */}
//       <div className="mt-4">
//         {/* Add Fee Elements Table */}
//         <div className="add-fee-elements ">
//           <h5>Add Fee Elements</h5>
//           {/* Add Bootstrap table-responsive class for responsive behavior */}
//           <div className="table-responsive">
//             <table className="table mt-2 table-bordered ">
//               <thead>
//                 <tr>
//                   <th style={{ width: "200px" }}>Element Name</th>
//                   <th style={{ width: "180px" }}>Frequency</th>
//                   <th>Amount</th>
//                   {/* <th>Period 1</th>
//                   <th>Period 2</th>
//                   <th>Period 3</th>
//                   <th>Period 4</th>
//                   <th>Period 5</th>
//                   <th>Period 6</th> */}
//                 </tr>
//               </thead>
//               <tbody>
//                 <tr>
//                   <td>
//                     <select
//                       className="detail"
//                       style={{ width: "200px" }}
//                       value={feeElement.element_type_id?.toString() || ""}
//                       onChange={(e) => {
//                         const selectedOption = elementNameOptions.find(
//                           (option) => option.value.toString() === e.target.value
//                         );
//                         setFeeElement((prev) => ({
//                           ...prev,
//                           element_type_id: selectedOption?.value || "",
//                           name: selectedOption?.label || "",
//                         }));
//                       }}
//                     >
//                       <option value="">Select Element</option>
//                       {elementNameOptions.map((option) => (
//                         <option key={option.value} value={option.value}>
//                           {option.label}
//                         </option>
//                       ))}
//                     </select>
//                   </td>
//                   <td>
//                     <select
//                       className="detail"
//                       style={{ width: "180px" }}
//                       value={feeElement.frequency?.toString() || ""}
//                       onChange={(e) => {
//                         const selectedOption = frequencyOptions.find(
//                           (option) => option.value.toString() === e.target.value
//                         );
//                         handleFrequencyChange(selectedOption || null);
//                       }}
//                     >
//                       <option value="">Select Frequency</option>
//                       {frequencyOptions.map((option) => (
//                         <option key={option.value} value={option.value}>
//                           {option.label}
//                         </option>
//                       ))}
//                     </select>
//                   </td>
//                   <td>
//                     <input
//                       type="text"
//                       id="amount"
//                       className="form-control detail"
//                       placeholder="Amount"
//                       value={feeElement.amount}
//                       inputMode="numeric"
//                       pattern="[0-9]*"
//                       onChange={(e) =>
//                         setFeeElement((prev) => ({
//                           ...prev,
//                           amount:
//                             e.target.value === ""
//                               ? ""
//                               : parseFloat(e.target.value),
//                         }))
//                       }
//                     />
//                   </td>
//                   {/* {Array.from({ length: 6 }).map((_, idx) => (
//                     <td key={idx}>
//                       <Select
//                         options={periodOptions}
//                         value={periodOptions.find(
//                           (option) => option.value === feeElement.periods[idx]
//                         )}
//                         isDisabled={disabledPeriods[idx]}
//                         onChange={(selectedOption) => {
//                           const updatedPeriods = [...feeElement.periods];
//                           updatedPeriods[idx] = selectedOption
//                             ? selectedOption.value
//                             : null;
//                           setFeeElement({
//                             ...feeElement,
//                             periods: updatedPeriods,
//                           });
//                         }}
//                       />
//                     </td>
//                   ))} */}

//                   {/* {Array.from({ length: 6 }).map((_, idx) => (
//                     <td key={idx}>
//                       <Select
//                         options={periodOptions}
//                         className="detail"
//                         value={
//                           periodOptions.find(
//                             (option) => option.value === feeElement.periods[idx]
//                           ) || null
//                         } // Handle null selection for each period
//                         isDisabled={disabledPeriods[idx]}
//                         onChange={(selectedOption) => {
//                           const updatedPeriods = [...feeElement.periods];
//                           updatedPeriods[idx] = selectedOption
//                             ? selectedOption.value
//                             : null;

//                           setFeeElement({
//                             ...feeElement,
//                             periods: updatedPeriods,
//                           });
//                         }}
//                         classNamePrefix="react-select"
//                         placeholder="Select"
//                       />
//                     </td>
//                   ))} */}
//                 </tr>
//                 {/* Add Button directly below the Period 6 column */}
//                 <tr>
//   <td colSpan={3} className="text-center">
//     <button
//       className="btn btn-primary"
//       onClick={handleAddFeeElement}
//       style={{ width: "150px" }}
//     >
//       Add
//     </button>
//   </td>
// </tr>
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Fee Structure Details Table */}
//         {feeStructure.length > 0 && (
//           <div className="fee-structure-details mt-4 mx-2">
//             <h5>Fee Structure Details</h5>
//             {/* Add Bootstrap table-responsive class for responsive behavior */}
//             <div className="table-responsive">
//               <table className="table mt-2 table-bordered">
//                 <thead>
//                   <tr>
//                     <th>Element Name</th>
//                     <th>Frequency</th>
//                     <th>Amount</th>
//                     {/* <th>Period 1</th>
//                     <th>Period 2</th>
//                     <th>Period 3</th>
//                     <th>Period 4</th>
//                     <th>Period 5</th>
//                     <th>Period 6</th> */}
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {feeStructure.map((element, index) => (
//                     <tr key={index}>
//                       <td>{element.name}</td>
//                       <td>
//                         {frequencyOptions.find(
//                           (option) => option.value === element.frequency
//                         )?.label || ""}
//                       </td>
//                       <td>{element.amount}</td>
//                       {element.periods.map((period, periodIndex) => {
//                         const periodName =
//                           periodOptions.find(
//                             (option) => option.value === period
//                           )?.label || "";
//                         return <td key={periodIndex}>{periodName}</td>;
//                       })}
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default AdmFeeStructure;














